/**
 * useAddressForm — Hook quản lý địa chỉ hỗ trợ 2 loại đơn vị hành chính:
 *   - 'old': Tỉnh → Quận/Huyện → Phường/Xã
 *   - 'new': Tỉnh → Phường/Xã (bỏ qua cấp Quận/Huyện)
 *
 * Cung cấp:
 *   - Danh sách provinces/districts/wards
 *   - Handlers: onProvinceChange, onDistrictChange, onWardChange
 *   - Toggle areaType với tự động reset cascade
 *   - initFromAddress: khởi tạo từ UserAddress/GuestInfo hiện có
 */
import { Area } from '@/constants'
import type { Province } from '@/types'
import { api } from '@/utils'
import { useCallback, useState } from 'react'

export type AreaType = 'old' | 'new'

export interface AddressValues {
  areaType: AreaType
  provinceCode: string
  districtCode: string
  wardCode: string
  address: string
}

export interface AddressSource {
  areaType?: AreaType | string
  provinceCode?: string
  districtCode?: string
  wardCode?: string
  address?: string
}

interface UseAddressFormOptions {
  /** Called when any field changes — dùng để sync với Formik/state ngoài */
  onChange?: (values: Partial<AddressValues>) => void
}

const IAM_BASE = process.env.NEXT_PUBLIC_IAM_API_URL

async function fetchAreas(url: string): Promise<Province[]> {
  const res = await api(url, { method: 'get', baseURL: IAM_BASE })
  if (res instanceof Error || !res?.data) return []
  return res.data as Province[]
}

export function useAddressForm(opts: UseAddressFormOptions = {}) {
  const { onChange } = opts

  // ─── Area type ────────────────────────────────────────────────────
  const [areaType, setAreaTypeState] = useState<AreaType>('old')

  // ─── Data ─────────────────────────────────────────────────────────
  const [provinces, setProvinces] = useState<Province[]>([])
  const [districts, setDistricts] = useState<Province[]>([])
  const [wards, setWards] = useState<Province[]>([])
  const [wardDistricts, setWardDistricts] = useState<Province[]>([])

  // ─── Loading ──────────────────────────────────────────────────────
  const [isLoadingProvinces, setIsLoadingProvinces] = useState(false)
  const [isLoadingDistricts, setIsLoadingDistricts] = useState(false)
  const [isLoadingWards, setIsLoadingWards] = useState(false)

  // ─── Fetch helpers ────────────────────────────────────────────────

  /** Tải danh sách tỉnh theo loại đơn vị hành chính */
  const loadProvinces = useCallback(async (type: AreaType = 'old') => {
    setIsLoadingProvinces(true)
    const data = await fetchAreas(
      `api/area?level=${Area.PROVINCE}&areaType=${type}&sort=order`
    )
    setProvinces(data)
    setIsLoadingProvinces(false)
    return data
  }, [])

  /** Tải danh sách quận/huyện (chỉ dùng khi areaType=old) */
  const loadDistricts = useCallback(async (provinceCode: string) => {
    if (!provinceCode) return
    setIsLoadingDistricts(true)
    const data = await fetchAreas(
      `api/area?level=${Area.DISTRICT}&parentCode=${provinceCode}&areaType=old`
    )
    setDistricts(data)
    setIsLoadingDistricts(false)
    return data
  }, [])

  /** Tải danh sách phường/xã
   *  - old: parentCode = districtCode
   *  - new: parentCode = provinceCode, areaType=new
   */
  const loadWards = useCallback(
    async (parentCode: string, type: AreaType = 'old') => {
      if (!parentCode) return
      setIsLoadingWards(true)
      const query =
        type === 'new'
          ? `api/area?level=${Area.WARD}&parentCode=${parentCode}&areaType=new`
          : `api/area?level=${Area.WARD}&parentCode=${parentCode}`
      const data = await fetchAreas(query)
      setWards(data)
      setIsLoadingWards(false)
      return data
    },
    []
  )

  /** Tải danh sách Quận/Huyện - Phường/Xã gộp (cho dropdown chọn nhanh) */
  const loadWardDistricts = useCallback(async (provinceCode: string) => {
    if (!provinceCode) return
    setIsLoadingWards(true)
    const data = await fetchAreas(`api/area/${provinceCode}/ward`)
    setWardDistricts(data)
    setIsLoadingWards(false)
    return data
  }, [])

  // ─── Handlers ────────────────────────────────────────────────────

  /**
   * Xử lý chọn Tỉnh/Thành:
   * - old: load district → reset district & ward
   * - new: load ward trực tiếp từ province → reset ward
   */
  const onProvinceChange = useCallback(
    async (provinceCode: string) => {
      setDistricts([])
      setWards([])
      const partialChange: Partial<AddressValues> = {
        provinceCode,
        districtCode: '',
        wardCode: '',
      }

      if (areaType === 'old') {
        await loadDistricts(provinceCode)
      } else {
        await loadWards(provinceCode, 'new')
      }

      onChange?.(partialChange)
      return partialChange
    },
    [areaType, loadDistricts, loadWards, onChange]
  )

  /**
   * Xử lý chọn Quận/Huyện (chỉ khi areaType=old):
   * - load ward của district đó → reset ward
   */
  const onDistrictChange = useCallback(
    async (districtCode: string) => {
      setWards([])
      const partialChange: Partial<AddressValues> = {
        districtCode,
        wardCode: '',
      }
      if (districtCode) {
        await loadWards(districtCode, 'old')
      }
      onChange?.(partialChange)
      return partialChange
    },
    [loadWards, onChange]
  )

  /** Xử lý chọn Phường/Xã */
  const onWardChange = useCallback(
    (wardCode: string) => {
      onChange?.({ wardCode })
    },
    [onChange]
  )

  /**
   * Toggle loại đơn vị hành chính:
   * - Reset toàn bộ cascade
   * - Load lại provinces theo type mới
   * Trả về các field cần reset để caller sync với form state
   */
  const onAreaTypeChange = useCallback(
    async (newType: AreaType) => {
      setAreaTypeState(newType)
      setDistricts([])
      setWards([])
      await loadProvinces(newType)
      const resetFields: Partial<AddressValues> = {
        areaType: newType,
        provinceCode: '',
        districtCode: '',
        wardCode: '',
      }
      onChange?.(resetFields)
      return resetFields
    },
    [loadProvinces, onChange]
  )

  /**
   * Khởi tạo dữ liệu địa chỉ từ UserAddress / GuestInfo đã có.
   * Tự động fetch đúng sequence dựa trên areaType.
   */
  const initFromAddress = useCallback(
    async (source: AddressSource) => {
      const type = (source.areaType as AreaType) || 'old'
      setAreaTypeState(type)
      await loadProvinces(type)

      if (type === 'old') {
        if (source.provinceCode) await loadDistricts(source.provinceCode)
        if (source.districtCode) await loadWards(source.districtCode, 'old')
      } else {
        if (source.provinceCode) await loadWards(source.provinceCode, 'new')
      }
    },
    [loadProvinces, loadDistricts, loadWards]
  )

  /** Reset về trạng thái ban đầu */
  const reset = useCallback((type: AreaType = 'old') => {
    setAreaTypeState(type)
    setProvinces([])
    setDistricts([])
    setWards([])
  }, [])

  return {
    // Data
    provinces,
    districts,
    wards,
    wardDistricts,
    areaType,

    // Loading flags
    isLoadingProvinces,
    isLoadingDistricts,
    isLoadingWards,
    isLoading: isLoadingProvinces || isLoadingDistricts || isLoadingWards,

    // Fetch helpers (raw)
    loadProvinces,
    loadDistricts,
    loadWards,
    loadWardDistricts,

    // Handlers with cascade reset (dùng trong form)
    onAreaTypeChange,
    onProvinceChange,
    onDistrictChange,
    onWardChange,
    initFromAddress,
    reset,
  }
}
